import { Metadata } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { TeamMemberCard } from '@/components/team-member-card';
import { localizeSpelling } from "@/lib/spelling-localization";
import { BackgroundVideo } from '@/components/background-video';

// In a server component, we don't use hooks to access params
export default function AboutPage({ params }: { params?: { locale?: string } }) {
  // Fallback to 'en' if locale is not provided
  const currentLocale = params?.locale || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  return (
    <div>
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Video */}
        <BackgroundVideo src="https://pub-33ea9fef23824733bfd34c2ea86136dc.r2.dev/hero.mp4" />
        
        {/* Dark overlay for better text readability */}
        <div className="absolute top-0 left-0 w-full h-full bg-black/50 z-10"></div>
        
        <div className="text-center max-w-4xl mx-auto relative z-20">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-white via-cyan-200 to-blue-200 bg-clip-text text-transparent mb-6 drop-shadow-2xl leading-tight py-2">
            {localize("Building the Future of AI")}
          </h1>
          <p className="text-xl text-gray-100 mb-12 drop-shadow-lg font-medium">
            {localize("At OpenLabs Research, we're pioneering advanced AI research and development to solve complex problems and create positive impact across industries.")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="rounded-full">
              <Link href={`/${currentLocale}/services/`}>{localize("Our Services")}</Link>
            </Button>
            <Button variant="outline" size="lg" className="rounded-full text-white border-white/30 hover:bg-white/10" asChild>
              <Link href={`/${currentLocale}/contact`}>{localize("Contact Us")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">{localize("Our Mission")}</h2>
            <p className="text-xl text-muted-foreground">
              {localize("To advance artificial intelligence research and develop practical AI solutions that empower businesses and improve lives worldwide.")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              {
                title: "Research",
                description: "Pushing the boundaries of AI through cutting-edge research in machine learning, computer vision, and natural language processing."
              },
              {
                title: "Innovation",
                description: "Transforming research into practical applications that solve real-world problems across various industries."
              },
              {
                title: "Impact",
                description: "Creating AI solutions that make a meaningful difference in businesses, society, and people's lives."
              }
            ].map((item, index) => (
              <div key={index} className="bg-background p-8 rounded-xl shadow-sm border">
                <h3 className="text-xl font-semibold mb-3">{localize(item.title)}</h3>
                <p className="text-muted-foreground">{localize(item.description)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Executive Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">{localize("OpenLabs Research Leadership")}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {localize("Leading experts in AI research, business development, and strategic innovation.")}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                name: "Nim Hewage",
                role: "Founder",
                bio: "13+ years of experience in AI Research, Data Science, Computer Vision, Automation, Robotics and end-to-end Solution Development. Leads the Technical Direction and Innovation Strategy at OpenLabs Research.",
                image: "/images/team/nim-hewage.jpg"
              },
              {
                name: "Chathu Hewage",
                role: "Country Director, New Zealand",
                bio: "Expert in Entrepreneurship and Business Development, Overseeing Regional Operations, Strategic Partnerships, and Market Growth across New Zealand.",
                image: "/images/team/chathu-hewage.jpg"
              }
            ].map((member, index) => (
              <TeamMemberCard key={index} {...member} />
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">{localize("Our Values")}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {localize("Guiding principles that shape our work and culture.")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Excellence",
                description: "We pursue the highest standards in everything we do, from research to product development."
              },
              {
                title: "Collaboration",
                description: "We believe in the power of teamwork and diverse perspectives to drive innovation."
              },
              {
                title: "Integrity",
                description: "We're committed to ethical AI development and transparent practices."
              },
              {
                title: "Impact",
                description: "We measure our success by the positive change we create in the world."
              }
            ].map((value, index) => (
              <div key={index} className="text-center p-6 bg-background rounded-xl shadow-sm border">
                <h3 className="text-xl font-semibold mb-3">{localize(value.title)}</h3>
                <p className="text-muted-foreground">{localize(value.description)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6">{localize("Join Us in Shaping the Future of AI")}</h2>
          <p className="text-xl text-muted-foreground mb-8">
            {localize("Whether you're a researcher, engineer, or AI enthusiast, we'd love to hear from you.")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button size="lg" className="rounded-full" asChild>
              <Link href={`/${currentLocale}/services/`}>{localize("Our Services")}</Link>
            </Button>
            <Button variant="outline" size="lg" className="rounded-full" asChild>
              <Link href={`/${currentLocale}/contact`}>{localize("Get in Touch")}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export function generateStaticParams() {
  // This ensures static generation works with the catch-all locale route
  return [];
}
